import Layout from '@src/components/Layout';
import dayjs from 'dayjs';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Accordion, Button, Dimmer, Header, Icon, Label, Loader, Popup, Segment, Tab, Table, Transition } from 'semantic-ui-react';
import { PriceCalculatorService } from '~/api/services/priceCalculator';
import ProfileSettingsService from '~/api/services/profileSettings';
import CustomIcon from '~/components/CustomIcon';
import CustomInput from '~/components/CustomInput';
import Dialog from '~/components/Dialog';
import notify from '~/components/Notify';
import TwoFaComp from '~/components/TwoFaComp';
import { BASE_PRICE_TEXT_SLUG, LIGHT_USER_ROLE_SLUG, MILK_MONEY_ACCOUNT_SLUG } from '~/Constants';
import { useAuth } from '~/routes/components/AuthProvider';
import { capitalizeFirstLetter, formatNumber, handleErrors } from '~/utils/helpers';
import MilkTypes from './MilkTypes';
import PartialPayment from './PartialPayment';
import { FarmerGroupMonthsWrapper, StyledAccordionTitle, StyledNoResultsMessage } from './style';

const PriceCalculator: React.FunctionComponent = () => {
  const { systemData, systemData: { logged: { roleSlug }, modules } } = useAuth()
  const { t } = useTranslation();
 
  const tfaPublishBasePrice = systemData?.roleSettings?.tfaPublishBasePrice === 1
  const lightUserLogged = roleSlug === LIGHT_USER_ROLE_SLUG
  const tfaType = systemData?.logged?.tfaType
  const basePriceText = systemData?.texts.find(text => text.slug === BASE_PRICE_TEXT_SLUG)
  const translationBasePrice = basePriceText?.translation || basePriceText.label

  const [isBusy, setIsBusy] = useState(false)
  const [activeIndex, setActiveIndex] = useState(0)
  const [isBusyContent, setIsBusyContent] = useState(false)
  const [farmerGroups, setFarmerGroups] = useState([])
  const [activeAccordionIndex, setActiveAccordionIndex] = useState(-1)
  const [farmerGroupMonths, setFarmerGroupMonths] = useState([])
  const [publishData, setPublishData] = useState(null)
  const [publishedMonthEditData, setPublishedMonthEditData] = useState(null)
  const [newPrice, setNewPrice] = useState('')
  const [correctTfaCode, setCorrectTfaCode] = useState(false)
  const [tfaCode, setTfaCode] = useState('')

  useEffect(() => {
    fetchPriceData()
  }, [])

  useEffect(() => {
    if (activeAccordionIndex >= 0 && farmerGroups.length > 0) {
      fetchFarmerGroupMonths()
    }
  }, [activeAccordionIndex])

  async function fetchFarmerGroupMonths() {
    const farmerGroup = farmerGroups[activeAccordionIndex]
    setIsBusyContent(true)
    try {
      let response
      if (activeIndex === 0) {
        //base price
        response = await PriceCalculatorService.getPriceCalculatorMonths({ farmerGroupId: farmerGroup?.id })
        setFarmerGroupMonths(response.data.map(item => ({
          date: item,
          price: ''
        })))
      } else {
        //published base price
        response = await PriceCalculatorService.getPriceCalculatorPublishedMonths({ farmerGroupId: farmerGroup?.id })
        setFarmerGroupMonths(response.data)
      }
    } catch (error) {
      if (error.response.data) {
        notify(error.response.data.message, 'error')
      } else {
        notify(t('General error'), 'error')
      }
    }
    setIsBusyContent(false)
  }

  async function fetchPriceData() {
    setIsBusy(true)
    try {
      const response = await PriceCalculatorService.getPriceCalculatorSettings()
      setFarmerGroups(response.data)
    } catch (error) {
      if (error.response.data) {
        notify(error.response.data.message, 'error')
      } else {
        notify(t('General error'), 'error')
      }
    }
    setIsBusy(false)
  }

  function handleFarmerGroupClick(e, titleProps) {
    const { index } = titleProps
    setActiveAccordionIndex(activeAccordionIndex === index ? -1 : index);
  }

  function handleChangeInput(value, index) {
    const monthsTemp = [...farmerGroupMonths]
    monthsTemp[index].price = value
    setFarmerGroupMonths(monthsTemp)
  }

  async function handleUpdateFarmerGroupMonths() {
    const farmerGroup = farmerGroups[activeAccordionIndex]
    const monthPriceChanged = farmerGroupMonths.filter(month => !!month.price)
    monthPriceChanged.forEach(month => {
      month.price = month.price.replace(',', '.')
    })
    setIsBusyContent(true)
    try {
      await PriceCalculatorService.updatePriceCalculatorMonths({
        farmerGroupId: farmerGroup?.id,
        months: monthPriceChanged,
        code: tfaCode
      })
      setTfaCode('')
      setCorrectTfaCode(false)
      setFarmerGroupMonths(farmerGroupMonths.filter(month => !month.price))
      setPublishData(null)
      notify(t('{{translationBasePrice}} has been published', { translationBasePrice }), 'success')
    } catch (error) {
      if (error.response.data) {
        notify(error.response.data.message, 'error')
      } else {
        notify(t('General error'), 'error')
      }
    }
    setIsBusyContent(false)
  }

  async function handleUpdatePublishedFarmerGroup() {
    setIsBusyContent(true)
    try {
      await PriceCalculatorService.updatePriceCalculatorPublishedMonths({
        months: [{
          id: publishedMonthEditData?.id,
          price: newPrice.replace(',', '.')
        }],
        code: tfaCode
      })
      const farmerGroupMonthsTemp = [...farmerGroupMonths]
      const index = farmerGroupMonthsTemp.findIndex(farmerGroupMonth => farmerGroupMonth?.id === publishedMonthEditData?.id)
      if (index >= 0) {
        farmerGroupMonthsTemp[index].price = newPrice
        setFarmerGroupMonths(farmerGroupMonthsTemp)
      }
      setTfaCode('')
      setCorrectTfaCode(false)
      setPublishedMonthEditData(null)
      setNewPrice('')
      notify(t('{{translationBasePrice}} has been updated', { translationBasePrice }), 'success')
    } catch (error) {
      if (error.response.data) {
        notify(error.response.data.message, 'error')
      } else {
        notify(t('General error'), 'error')
      }
    }
    setIsBusyContent(false)
  }

  async function handleSendCode() {
    if (tfaType === 'email' || tfaType === 'sms') {
      setIsBusy(true)
      try {
        await ProfileSettingsService.sendCode()
        notify(t('Code sent'), 'success')
      } catch (error) {
        handleErrors(error)
      }
      setIsBusy(false)
    }
  }

  function renderNoResultsMessage() {
    return <StyledNoResultsMessage>
      <Segment placeholder>
        <Header icon>
          <Icon name='search' />
          {t('No results found....')}
        </Header>
        <Segment.Inline>
        </Segment.Inline>
      </Segment>
    </StyledNoResultsMessage>
  }

  function renderFarmerGroupMonths() {
    const editedItems = farmerGroupMonths.filter(month => !!month.price)
    return <FarmerGroupMonthsWrapper>
      {activeIndex === 0 && <div className='actions'>
        <Popup
          content={t('You can save the {{translationBasePrice}} for multiple months at a time.', { translationBasePrice })}
          trigger={<CustomIcon
            name='warning sign'
            size='large'
            color='yellow'
          />}
        />
        <Button
          onClick={() => {
            if (tfaPublishBasePrice) {
              handleSendCode()
            }
            setPublishData(farmerGroupMonths.filter(month => !!month.price))
          }}
          primary
          disabled={editedItems.length === 0 || (farmerGroupMonths.find(month => month.price >= 1000) !== undefined)}
        >
          {t('Update')}
        </Button>
      </div>
      }
      <Dimmer active={isBusyContent} inverted>
        <Loader style={{ position: 'fixed' }} active={isBusyContent} />
      </Dimmer>
      <Table celled>
        <Table.Header>
          <Table.Row>
            <Table.HeaderCell>{t('Trading month')}</Table.HeaderCell>
            <Table.HeaderCell>{translationBasePrice}</Table.HeaderCell>
            {activeIndex > 0 && !lightUserLogged && <Table.HeaderCell>{t('Actions')}</Table.HeaderCell>}
          </Table.Row>
        </Table.Header>
        <Table.Body>
          {farmerGroupMonths.length === 0 ? <Table.Row>
            <Table.Cell colSpan={2}>
              {renderNoResultsMessage()}
            </Table.Cell>
          </Table.Row> :
            farmerGroupMonths.map((month, index) => {
              return <Table.Row key={month.date}>
                <Table.Cell>
                  <Label ribbon>{capitalizeFirstLetter(dayjs(month?.date).format('MMM YYYY'))}</Label>
                </Table.Cell>
                <Table.Cell>
                  {activeIndex > 0 ? month?.price.includes(',') ? month?.price : formatNumber(month?.price) : <CustomInput
                    value={month.price}
                    allowDecimals
                    limit={999.99}
                    disabled={lightUserLogged}
                    onChange={(value) => handleChangeInput(value, index)}
                    style={{ width: '100%' }}
                    placeholder={capitalizeFirstLetter(dayjs(month?.date).format('MMM YYYY'))}
                  />
                  }
                </Table.Cell>
                {activeIndex > 0 && !lightUserLogged && < Table.Cell >
                  <Popup
                    content={t('Edit {{translationBasePrice}}', { translationBasePrice })}
                    trigger={<CustomIcon
                      onClick={() => {
                        setPublishedMonthEditData(month)
                        if (tfaPublishBasePrice) {
                          handleSendCode()
                        }
                      }}
                      name='pencil'
                      size='large'
                    />}
                  />
                </Table.Cell>
                }
              </Table.Row>
            })}
        </Table.Body>
      </Table>
    </FarmerGroupMonthsWrapper >
  }

  function renderFarmerGroups() {
    return farmerGroups.map((farmerGroup, index) => {
      return <Accordion
        key={farmerGroup?.id + index}
        styled
        style={{ width: 'calc(100% - 40px)', margin: 10 }}
      >
        <Accordion.Title
          onClick={handleFarmerGroupClick}
          index={index}
          active={activeAccordionIndex === index}
          style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between' }}>
          <StyledAccordionTitle>{farmerGroup?.name}</StyledAccordionTitle>
          <Icon name={activeAccordionIndex === index ? 'minus' : 'plus'} />
        </Accordion.Title>
        <Transition visible={activeAccordionIndex === index && !isBusyContent} animation='slide down' duration={200}>
          <Accordion.Content
            active={activeAccordionIndex === index}
            content={!isBusyContent && renderFarmerGroupMonths()}
          />
        </Transition>
      </Accordion>
    })
  }

  function handleTabChange(activeIndex) {
    setActiveAccordionIndex(-1)
    setActiveIndex(activeIndex)
  }

  function renderTab() {
    const hasMilkMoneyModule = Boolean(modules.find(module => module.slug === MILK_MONEY_ACCOUNT_SLUG))
    const panes = [
      {
        menuItem: t('Milk Types'),
        render: () => <MilkTypes />,
        display: true
      },
      {
        menuItem: t('Partial Payment'),
        render: () => <PartialPayment />,
        display: hasMilkMoneyModule
      },
      {
        menuItem: translationBasePrice,
        render: () => renderFarmerGroups(),
        display: true
      },
      {
        menuItem: t('Published {{translationBasePrice}}', { translationBasePrice }),
        render: () => renderFarmerGroups(),
        display: true
      }
    ].filter(pane => pane.display)

    return (
      <Tab
        menu={{ secondary: true, pointing: true }}
        panes={panes}
        onTabChange={(_, data) => handleTabChange(data.activeIndex)}
      />
    );
  }

  function renderPublish(data, farmerGroupName) {
    return data.map(publishedData => {
      return <Table.Row key={publishedData.date}>
        <Table.Cell>{farmerGroupName || ''}</Table.Cell>
        <Table.Cell>{capitalizeFirstLetter(dayjs(publishedData?.date).format('MMM YYYY'))}</Table.Cell>
        <Table.Cell>{publishedData?.price.includes(',') ? publishedData?.price : formatNumber(publishedData?.price)}</Table.Cell>
      </Table.Row>
    })
  }

  function renderEditPublish(data, farmerGroupName) {
    return <Table.Row>
      <Table.Cell>{farmerGroupName || ''}</Table.Cell>
      <Table.Cell>{capitalizeFirstLetter(dayjs(data?.date).format('MMM YYYY'))}</Table.Cell>
      <Table.Cell>{data?.price.includes(',') ? data?.price : formatNumber(data?.price)}</Table.Cell>
      <Table.Cell>
        <CustomInput
          value={newPrice}
          allowDecimals
          onChange={(value) => setNewPrice(value)}
          placeholder={t('New price')}
        />
      </Table.Cell>
    </Table.Row>
  }

  function renderTable(data, publish = false) {
    const farmerGroup = farmerGroups[activeAccordionIndex]
    return <Table celled>
      <Table.Header>
        <Table.Row>
          <Table.HeaderCell>{t('Farmer group')}</Table.HeaderCell>
          <Table.HeaderCell>{t('Trading month')}</Table.HeaderCell>
          <Table.HeaderCell>{t('Price')}</Table.HeaderCell>
          {!publish && <Table.HeaderCell>{t('New price')}</Table.HeaderCell>}
        </Table.Row>
      </Table.Header>
      <Table.Body>
        {publish ? renderPublish(data, farmerGroup?.name) : renderEditPublish(data, farmerGroup?.name)}
      </Table.Body>
    </Table>
  }

  function renderPublishedMonthEditDialog() {
    return <Dialog
      handleClose={() => {
        setTfaCode('')
        setCorrectTfaCode(false)
        setNewPrice('')
        setPublishedMonthEditData(null)
      }}
      isLoading={isBusyContent}
      disableSubmit={tfaPublishBasePrice ? !correctTfaCode || !newPrice : newPrice === ''}
      handleSubmit={() => handleUpdatePublishedFarmerGroup()}
      open={Boolean(publishedMonthEditData)}
      textHeader={t('{{translationBasePrice}} update and publish', { translationBasePrice })}
      textContent={<>
        {renderTable(publishedMonthEditData)}
        {tfaPublishBasePrice && <TwoFaComp
          onChangeTfaCode={(code) => setTfaCode(code)}
          onChangeCorrectTfaCode={(correctTfaCode) => setCorrectTfaCode(correctTfaCode)}
        />}
      </>}
      textClose={t('Abort')}
      textSubmit={t('Update and publish', { translationBasePrice })}
      type={'dialog'}
    />
  }

  function renderPublishDataDialog() {
    return <Dialog
      open={Boolean(publishData)}
      htmlContent
      disableSubmit={tfaPublishBasePrice ? !correctTfaCode : false}
      handleClose={() => {
        setTfaCode('')
        setCorrectTfaCode(false)
        setPublishData(null)
      }}
      isLoading={isBusyContent}
      handleSubmit={() => handleUpdateFarmerGroupMonths()}
      textHeader={t('Publish {{translationBasePrice}}', { translationBasePrice })}
      textClose={t('Abort')}
      textContent={<>
        <div style={{ textAlign: 'center' }}>{t('You have set the following {{translationBasePrice}}:', { translationBasePrice })}</div>
        {renderTable(publishData, true)}
        {tfaPublishBasePrice && <TwoFaComp
          onChangeTfaCode={(code) => setTfaCode(code)}
          onChangeCorrectTfaCode={(correctTfaCode) => setCorrectTfaCode(correctTfaCode)}
        />}
      </>}
      textSubmit={t('Publish', { translationBasePrice })}
    />
  }

  return <Layout
    isBusy={isBusy}
    title={translationBasePrice}
    useSpinner
    childContainerSize={'300px'}
    fontSizeHeader={'18px'}
  >
    {publishData && renderPublishDataDialog()}
    {publishedMonthEditData && renderPublishedMonthEditDialog()}
    {renderTab()}
  </Layout>;
};

export default PriceCalculator;
